/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import contextImg from 'assets/images/context.jpg'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Объекты</h2>
    <p className={styles.description}>
      При описании какой-то сущности: пользователя, гостя, продукта, необходима структрура позволяющая хранить все
      характеристики связно и в одном месте. В большинстве языков программирование для этого есть типа данных{' '}
      <code className={styles.code}>Object (объект)</code>.
    </p>
    <p className={styles.description}>
      С точки зрения языка программирования, объект это набор свойств и методов. С точки зрения разработчика объект это
      абстракция, описывающая какие-то данные и функции для обработки этих данных.
    </p>
    <p className={styles.description}>
      Какие именно свойства (данные) и методы (функции обработки данных) должны быть в объекте решается каждый раз с
      учетом конкретной задачи.
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Создание объекта</h3>
      <p className={styles.description}>
        <b>Объект</b> — ассоциативный массив, тип данных, хранящий свойства (properties) и методы (methods).
      </p>
      <p className={styles.description}>
        Создание объекта через литерал похоже на создание массива, только используются фигурные{' '}
        <code className={styles.code}>{`{}`}</code>, а не квадратные скобки.
      </p>
      <CopyBlock text={`const hotel = {};`} language="javascript" showLineNumbers={false} theme={ocean} />
      <p className={styles.description}>
        При создании объект может быть инициализирован свойствами, каждое из которых описывается парами{' '}
        <code className={styles.code}>ключ:значение (key:value)</code>. Свойства разделяются запятой.
      </p>
      <p className={styles.description}>
        <b>Ключ</b> — это всегда строка. Значением могут быть любые типы: примитивы, массивы, объекты, були, функции и
        т. п.
      </p>
      <p className={styles.description}>Правила именования ключей просты: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>Если ключ заключен в кавычки, то это может быть произвольная строка.</li>
        <li className={styles.list__item}>
          Если кавычек нет, то вступают ограничения — имя без пробелов, начинающаеся на букву или символы{' '}
          <code className={styles.code}>_</code> и <code className={styles.code}>$</code>.
        </li>
      </ul>
      <p className={styles.description}>
        Например, в программе для туроператора понадобится объект, описывающий отель: название, уровень сервиса,
        вместимость и т. п.
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.2 - Доступ к свойствам</h3>
      <p className={styles.description}>
        Доступ к свойству объекта, для того чтобы получить или присвоить ему значение, может быть записан двумя
        способами:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>object.key</code> — через точку.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>object["key"]</code> — через квадратные скобки с именем ключа в кавычках.
        </li>
      </ul>
      <p className={styles.description}>На место обращения будет возвращено значение свойства с таким ключом. </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

console.log(hotel.name); // Resort Hotel
console.log(hotel['name']); // Resort Hotel

hotel.name = 'Coastline Resort';
console.log(hotel.name); // Coastline Resort
console.log(hotel['name']); // Coastline Resort

hotel['name'] = 'Stardust Hotel';
console.log(hotel.name); // Stardust Hotel
console.log(hotel['name']); // Stardust Hotel`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Если при записи значения по ключу, такого ключа в объекте нет, он будет создан.
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

hotel.address = '1, Beach ave.';
hotel['manager'] = 'Chuck Norris';

console.log(hotel.address); // 1, Beach ave.
console.log(hotel.manager); // Chuck Norris`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.3 - Удаление свойств</h3>
      <p className={styles.description}>
        Удалить свойство объекта можно с помощью оператора <code className={styles.code}>delete</code>. Удаление свойств
        операция крайне редкая, чаще всего свойства только добавляются или изменяются.
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

delete hotel.name;
console.log(hotel); // {stars: 5, capacity: 100}

delete hotel['stars'];
console.log(hotel); // {capacity: 100}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.4 - Отсутствующие свойства</h3>
      <p className={styles.description}>
        Отсутствующее свойство, при обращении к нему по ключу, возвращает <code className={styles.code}>undefined</code>
        .
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  guests: ['mango', 'poly', 'ajax'],
};

console.log(hotel.stars); // 5
console.log(hotel.pool); // undefined`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.5 - Короткие свойства</h3>
      <p className={styles.description}>
        Часто есть переменные, например, <code className={styles.code}>name</code> и{' '}
        <code className={styles.code}>stars</code>, и мы хотим использовать их в объекте так, чтобы имя перемнной стало
        ключом, а значение переменной значением свойства. При объявлении объекта в этом случае достаточно указать только
        имя свойства, а значение будет взято из переменной с аналогичным именем. Это синтаксис{' '}
        <b>коротких свойств (shorthand properties)</b>.
      </p>
      <CopyBlock
        text={`let name = 'Resort Hotel';
let stars = 5;

// Эта ES5 запись избыточна
const es5hotel = {
  name: name,
  stars: stars,
  capacity: 100,
};

/*
 * ES6 Shorthand properties
 * Имя ключа будет аналогично имени переменной
 * Значение свойства будет равно значению переменной
 */
const es6hotel = {
  name,
  stars,
  capacity: 100,
};

console.log(es6hotel); // {name: "Resort Hotel", stars: 5, capacity: 100}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.6 - Вычисляемые свойства</h3>
      <p className={styles.description}>
        Иногда необходимо добавить к объекту поле с ключом, значение которого мы заранее не знаем, он хранится как
        значение переменной. Раньше для этого необходимо было сначала создать объект, а потом добавлять свойства через
        квадратные скобки.
      </p>
      <CopyBlock
        text={`const key = 'person';
const object = {};

object[key] = 'Mango';
console.log(object); // {person: 'Mango'}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Используя вычисляемые свойства, можно избежать лишнего кода, и в некоторых случаях значительно его упросить.
        Значением вычисляемого свойства может быть любое валидное выражение.
      </p>
      <CopyBlock
        text={`const key = 'person';
const getKey = function() {
  return 'age';
};

// Computed properties
const object = {
  [key]: 'Mango',
  [getKey()]: 2,
};

console.log(object); // {person: 'Mango', age: 2}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.7 - Методы объекта</h3>
      <p className={styles.description}>
        Объекты хранят не только данные, но и функции для работы с этими данными. Если значение свойства это функция, то
        оно называется <b>методом объекта</b>.
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
  // Так метод объявлялся в ES5
  greetInES5: function() {
    console.log('Welcome!');
  },
  // Так метод объявляется в ES6
  greetInES6() {
    console.log('Welcome!');
  },
};

hotel.greetInEs5(); // Welcome!
hotel.greetInEs6(); // Welcome!`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Так как методы это обычные свойства, значение которых это функция, их можно добвлять или удалять в любой момент.
      </p>
      <CopyBlock
        text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
};

hotel.greet = function() {
  console.log('Welcome!');
};

hotel.greet(); // Welcome!`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.7.1 - Доступ к объекту через this</h4>
        <p className={styles.description}>
          Методы используются для работы со свойствами объекта, их изменения. Для доступа к текущему объекту, в методе
          используется не имя объекта, а ключевое слово <code className={styles.code}>this</code> - контекст. Значением{' '}
          <code className={styles.code}>this</code> является объект перед <code className={styles.code}>'точкой'</code>,
          в контексте которого вызван метод.
        </p>
        <CopyBlock
          text={`const hotel = {
  name: 'Resort Hotel',
  stars: 5,
  capacity: 100,
  showName() {
    console.log(this.name);
  },
  changeCapacity(value) {
    this.capacity = value;
  },
};

hotel.showName(); // Resort Hotel

hotel.changeCapacity(200);
console.log(hotel.capacity); // 200`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Значение <code className={styles.code}>this</code> определяется в момент вызова функции, а не в момент ее
          объявления. То есть во время вызова <code className={styles.code}>hotel.showName()</code>, в{' '}
          <code className={styles.code}>this</code> внутри функции <code className={styles.code}>showName</code> будет
          записана ссылка на объект <code className={styles.code}>hotel</code>.
        </p>
        <img className={styles.img} src={contextImg} alt="context" />
        <p className={styles.description}>
          Будет логично задуматься - почему бы не использовать при обращении к свойствам имя объекта. Дело в том, что
          имя объекта штука не надежная, методы одного объекта можно копировать в другой (с другим именем), а в будущем
          мы узнаем что часто при создании объекта мы заранее не знаем имени. Использование{' '}
          <code className={styles.code}>this</code> гарантирует, что функция работает именно с тем объектом, в контексте
          которого вызвана.
        </p>
        <p className={styles.notificationInfo}>
          Мы детально разберем ключевое слово <code className={styles.code}>this</code> и все его подводные камни в
          следующих модулях, а сейчас достаточно просто использовать <code className={styles.code}>this</code> при
          обращении к свойствами объекта в его методах.
        </p>
      </section>
    </section>
  </section>
))
